import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  Zap, 
  Building2, 
  Euro,
  FileText,
  Edit,
  Home,
  CheckCircle,
  XCircle,
  FolderOpen,
  Wrench,
  Plus
} from "lucide-react";
import ClientForm from "./ClientForm";
import DocumentManager from "./DocumentManager";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper pour formater les dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: fr });
  } catch (e) {
    return "N/A";
  }
};

// Helper pour les statuts
const getStatutBadge = (statut) => {
  const badges = {
    'prospect': <Badge variant="outline">Prospect</Badge>,
    'client': <Badge className="bg-green-100 text-green-800">Client</Badge>,
    'lost': <Badge variant="destructive">Perdu</Badge>
  };
  return badges[statut] || <Badge variant="outline">{statut}</Badge>;
};

const getStatutPipelineLabel = (statut) => {
  const labels = {
    'bdc_signe': '✅ BDC Signé',
    'envoi_financement': '💳 Envoyé en Financement',
    'attente_financement': '⏳ Attente Accord Financement',
    'depot_mairie': '🏛️ Dépôt Dossier Mairie',
    'attente_mairie': '⏳ Attente Autorisation Mairie',
    'demande_mpr': '📄 Demande MaPrimeRénov',
    'attente_octroi_mpr': '⏳ Attente Octroi MPR',
    'mpr_accordee': '✅ MPR Accordée',
    'a_programmer': '📅 À Programmer en Installation',
    'installation_programmee': '🔧 Installation Programmée',
    'en_installation': '🚧 Installation en Cours',
    'installation_terminee': '✅ Installation Terminée',
    'attente_deblocage': '💰 Attente Déblocage Fonds',
    'paiement_partiel': '💵 Paiement Partiel Reçu',
    'paye_finalise': '✅ Client Payé et Finalisé'
  };
  return labels[statut] || statut || '-';
};

const ClientDetails = ({ client, onClose, onUpdate, onDelete }) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [currentClient, setCurrentClient] = useState(client);
  const [telepros, setTelepros] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [clientProjets, setClientProjets] = useState([]);
  const [loadingProjets, setLoadingProjets] = useState(false);
  const [clientSAV, setClientSAV] = useState([]);
  const [showSAVDialog, setShowSAVDialog] = useState(false);
  const [savForm, setSavForm] = useState({ titre: '', description: '', priorite: 'normale', type_intervention: 'maintenance' });

  useEffect(() => {
    fetchUsers();
    fetchClientDetails();
  }, [client.id]);

  const fetchClientDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/${client.id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentClient(response.data);
    } catch (error) {
      console.error("Error fetching client details:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const telepro = response.data.filter(u => u.role === 'teleprospecteur' || u.role === 'telepro');
      const comm = response.data.filter(u => u.role === 'commercial');
      
      setTelepros(telepro);
      setCommerciaux(comm);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchClientProjets = async () => {
    setLoadingProjets(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/projets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const clientProjetsFiltered = response.data.filter(
        projet => projet.client_id === client.id
      );
      
      setClientProjets(clientProjetsFiltered);
    } catch (error) {
      console.error("Error fetching client projets:", error);
      toast.error("Erreur lors du chargement des projets");
    } finally {
      setLoadingProjets(false);
    }
  };

  const fetchClientSAV = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/interventions`, { headers: { Authorization: `Bearer ${token}` } });
      setClientSAV((res.data || []).filter(i => i.client_id === client.id));
    } catch (e) { console.error(e); }
  };

  const handleCreateSAV = async () => {
    if (!savForm.titre) { toast.error('Titre obligatoire'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/interventions`, {
        client_id: client.id,
        ...savForm,
        statut: 'planifiee',
        date_planifiee: new Date().toISOString()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ticket SAV créé');
      setShowSAVDialog(false);
      setSavForm({ titre: '', description: '', priorite: 'normale', type_intervention: 'maintenance' });
      fetchClientSAV();
    } catch (e) { toast.error('Erreur création SAV'); }
  };


  const handleSaveClient = async (clientData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/clients/${client.id}`, clientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Client mis à jour avec succès");
      setShowEditDialog(false);
      
      // Rafraîchir les détails
      await fetchClientDetails();
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Erreur lors de la mise à jour du client");
    }
  };

  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="w-4 h-4 text-gray-500 mt-0.5" />}
      <div className="flex-1">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-sm font-medium">{value || "N/A"}</p>
      </div>
    </div>
  );

  const getUserName = (userId, usersList) => {
    const user = usersList.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : userId;
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-2xl">
              {currentClient.nom} {currentClient.prenom}
            </CardTitle>
            <CardDescription>
              {currentClient.company_name && `${currentClient.company_name} • `}
              {getStatutBadge(currentClient.status)}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowEditDialog(true)} size="sm">
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="identite" className="w-full">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="identite">
                <User className="w-4 h-4 mr-2" />
                Identité
              </TabsTrigger>
              <TabsTrigger value="logement">
                <Home className="w-4 h-4 mr-2" />
                Logement
              </TabsTrigger>
              <TabsTrigger value="energie">
                <Zap className="w-4 h-4 mr-2" />
                Énergie
              </TabsTrigger>
              <TabsTrigger value="aides">
                <Euro className="w-4 h-4 mr-2" />
                Aides
              </TabsTrigger>
              <TabsTrigger value="commercial">
                <Building2 className="w-4 h-4 mr-2" />
                Commercial
              </TabsTrigger>
              <TabsTrigger value="projets" onClick={fetchClientProjets}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Projets
              </TabsTrigger>
              <TabsTrigger value="sav" onClick={fetchClientSAV}>
                <Wrench className="w-4 h-4 mr-2" />
                SAV
              </TabsTrigger>
              <TabsTrigger value="documents">
                <FileText className="w-4 h-4 mr-2" />
                Documents
              </TabsTrigger>
            </TabsList>

            {/* ONGLET IDENTITÉ */}
            <TabsContent value="identite" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informations Personnelles</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Nom" value={currentClient.nom} icon={User} />
                  <InfoRow label="Prénom" value={currentClient.prenom} icon={User} />
                  <InfoRow label="Email" value={currentClient.email} icon={Mail} />
                  <InfoRow label="Téléphone Portable" value={currentClient.telephone_portable} icon={Phone} />
                  <InfoRow label="Téléphone Fixe" value={currentClient.telephone_fixe} icon={Phone} />
                  {currentClient.source_prospect && (
                    <InfoRow label="Source Prospect" value={currentClient.source_prospect} icon={Zap} />
                  )}
                </CardContent>
              </Card>

              {currentClient.company_name && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Informations Entreprise</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <InfoRow label="Raison Sociale" value={currentClient.company_name} icon={Building2} />
                    <InfoRow label="SIREN" value={currentClient.siren} icon={FileText} />
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Adresse</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Adresse" value={currentClient.adresse} icon={MapPin} />
                  <InfoRow label="Code Postal" value={currentClient.code_postal} />
                  <InfoRow label="Ville" value={currentClient.ville} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informations Complémentaires</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Situation Familiale" value={currentClient.situation_familiale} />
                  <InfoRow label="Domaine d'Activité" value={currentClient.domaine_activite} />
                  <InfoRow label="Montant Crédit en Cours" value={currentClient.montant_credit_en_cours ? `${currentClient.montant_credit_en_cours} €` : "N/A"} />
                </CardContent>
              </Card>

              {(currentClient.type_contrat_monsieur || currentClient.revenus_mensuels_monsieur) && (
                <Card className="bg-blue-50">
                  <CardHeader>
                    <CardTitle className="text-lg">👨 Informations Monsieur</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <InfoRow label="Type de Contrat" value={currentClient.type_contrat_monsieur} />
                    <InfoRow label="Revenus Mensuels" value={currentClient.revenus_mensuels_monsieur ? `${currentClient.revenus_mensuels_monsieur} €` : "N/A"} />
                  </CardContent>
                </Card>
              )}

              {(currentClient.type_contrat_madame || currentClient.revenus_mensuels_madame) && (
                <Card className="bg-pink-50">
                  <CardHeader>
                    <CardTitle className="text-lg">👩 Informations Madame</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <InfoRow label="Type de Contrat" value={currentClient.type_contrat_madame} />
                    <InfoRow label="Revenus Mensuels" value={currentClient.revenus_mensuels_madame ? `${currentClient.revenus_mensuels_madame} €` : "N/A"} />
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ONGLET LOGEMENT */}
            <TabsContent value="logement" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Caractéristiques du Logement</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Type de Logement" value={currentClient.type_logement} icon={Home} />
                  <InfoRow label="Année de Construction" value={currentClient.annee_construction} icon={Calendar} />
                  <InfoRow label="Surface Habitable" value={currentClient.surface_habitable ? `${currentClient.surface_habitable} m²` : "N/A"} />
                  <InfoRow label="Nombre de Pièces" value={currentClient.nombre_pieces} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Chauffage Actuel</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Mode de Chauffage" value={currentClient.mode_chauffage_actuel} icon={Zap} />
                  <InfoRow label="Type de Chaudière" value={currentClient.type_chaudiere_actuelle} />
                  <InfoRow label="Année de la Chaudière" value={currentClient.annee_chaudiere} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Isolation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2">
                    {currentClient.isolation_combles ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className="text-sm">Isolation des Combles</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentClient.isolation_murs ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className="text-sm">Isolation des Murs</span>
                  </div>
                  <InfoRow label="Type de Fenêtres" value={currentClient.type_fenetres} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET ÉNERGIE */}
            <TabsContent value="energie" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Consommation Énergétique</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow 
                    label="Facture Énergie Annuelle" 
                    value={currentClient.facture_energie_annuelle ? `${currentClient.facture_energie_annuelle} €` : "N/A"} 
                    icon={Euro} 
                  />
                  <InfoRow 
                    label="Consommation Gaz Annuelle" 
                    value={currentClient.consommation_gaz_annuelle ? `${currentClient.consommation_gaz_annuelle} kWh` : "N/A"} 
                    icon={Zap} 
                  />
                  <InfoRow 
                    label="Consommation Électrique Annuelle" 
                    value={currentClient.consommation_elec_annuelle ? `${currentClient.consommation_elec_annuelle} kWh` : "N/A"} 
                    icon={Zap} 
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Performance Énergétique</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="DPE Actuel" value={currentClient.dpe_actuel} />
                  <InfoRow label="GES Actuel" value={currentClient.ges_actuel} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET AIDES */}
            <TabsContent value="aides" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">MaPrimeRénov'</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow 
                    label="Revenu Fiscal de Référence" 
                    value={currentClient.revenu_fiscal_reference ? `${currentClient.revenu_fiscal_reference} €` : "N/A"} 
                    icon={Euro} 
                  />
                  <InfoRow label="Nombre de Personnes au Foyer" value={currentClient.nombre_personnes_foyer} icon={User} />
                  <div className="flex items-center gap-2">
                    {currentClient.proprietaire ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className="text-sm">Propriétaire</span>
                  </div>
                  <InfoRow label="Occupant Depuis" value={currentClient.occupant_depuis} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET COMMERCIAL */}
            <TabsContent value="commercial" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Assignations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Téléprospecteur</p>
                    <p className="font-medium text-orange-800">
                      {currentClient.assigned_telepro ? getUserName(currentClient.assigned_telepro, telepros) : "Non assigné"}
                    </p>
                    {currentClient.commission_telepro && (
                      <p className="text-sm text-gray-600 mt-1">
                        Commission: {currentClient.commission_telepro} €
                      </p>
                    )}
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Commercial</p>
                    <p className="font-medium text-blue-800">
                      {currentClient.assigned_commercial_name || (currentClient.assigned_commercial ? getUserName(currentClient.assigned_commercial, commerciaux) : "Non assigné")}
                    </p>
                    {currentClient.commission_commercial && (
                      <p className="text-sm text-gray-600 mt-1">
                        Commission: {currentClient.commission_commercial} €
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Statut & Pipeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Statut</p>
                    {getStatutBadge(currentClient.status)}
                  </div>

                  {currentClient.statut_pipeline && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Statut Pipeline</p>
                      <Badge className="bg-purple-100 text-purple-800">
                        {getStatutPipelineLabel(currentClient.statut_pipeline)}
                      </Badge>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                    {currentClient.date_signature_bdc && (
                      <div>
                        <p className="text-xs text-gray-500">BDC Signé</p>
                        <p className="text-sm font-medium">{formatDate(currentClient.date_signature_bdc)}</p>
                      </div>
                    )}
                    {currentClient.date_installation_programmee && (
                      <div>
                        <p className="text-xs text-gray-500">Installation Programmée</p>
                        <p className="text-sm font-medium">{formatDate(currentClient.date_installation_programmee)}</p>
                      </div>
                    )}
                    {currentClient.date_paiement_final && (
                      <div>
                        <p className="text-xs text-gray-500">Paiement Final</p>
                        <p className="text-sm font-medium">{formatDate(currentClient.date_paiement_final)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Notes & Solvabilité</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <InfoRow label="Note de Solvabilité" value={currentClient.note_solvabilite} />
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Commentaires</p>
                    <p className="text-sm whitespace-pre-wrap">
                      {currentClient.commentaires || "Aucun commentaire"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ONGLET PROJETS */}
            <TabsContent value="projets" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Projets ({clientProjets.length})</h3>
                <Button 
                  onClick={() => {
                    window.location.href = `/projets?create=true&client_id=${client.id}`;
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Nouveau Projet
                </Button>
              </div>
              
              {loadingProjets ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
                </div>
              ) : clientProjets.length > 0 ? (
                <div className="space-y-4">
                  {clientProjets.map((projet) => {
                    const statutConfig = {
                      devis: { label: "Devis", color: "bg-blue-100 text-blue-800" },
                      accepte: { label: "Accepté", color: "bg-green-100 text-green-800" },
                      en_cours: { label: "En cours", color: "bg-orange-100 text-orange-800" },
                      pose_finalisee: { label: "Pose finalisée", color: "bg-teal-100 text-teal-800" },
                      termine: { label: "Terminé", color: "bg-emerald-100 text-emerald-800" },
                      annule: { label: "Annulé", color: "bg-red-100 text-red-800" }
                    };
                    
                    const typeConfig = {
                      photovoltaique: { label: "Photovoltaïque", icon: "☀️" },
                      pompe_chaleur: { label: "Pompe à chaleur", icon: "🔥" },
                      isolation: { label: "Isolation", icon: "🏠" },
                      chaudiere: { label: "Chaudière", icon: "⚡" }
                    };
                    
                    const statut = statutConfig[projet.statut] || statutConfig.devis;
                    const type = typeConfig[projet.type_projet] || { label: projet.type_projet, icon: "📋" };
                    
                    return (
                      <Card key={projet.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">{type.icon}</span>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {projet.titre || "Sans titre"}
                                </h3>
                                <Badge className={statut.color}>
                                  {statut.label}
                                </Badge>
                                <Badge variant="outline">
                                  {type.label}
                                </Badge>
                              </div>
                              
                              {projet.adresse_installation && (
                                <div className="flex items-center text-sm text-gray-600 mb-2">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  {projet.adresse_installation}, {projet.code_postal} {projet.ville}
                                </div>
                              )}

                              {projet.date_installation && (
                                <div className="flex items-center text-sm text-gray-600">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  Installation prévue: {formatDate(projet.date_installation)}
                                </div>
                              )}
                            </div>

                            <div className="text-right">
                              <div className="text-2xl font-bold text-red-600">
                                {new Intl.NumberFormat('fr-FR', {
                                  style: 'currency',
                                  currency: 'EUR'
                                }).format(projet.montant_ttc || 0)}
                              </div>
                              <div className="text-xs text-gray-500">TTC</div>
                            </div>
                          </div>

                          {projet.produits && projet.produits.length > 0 && (
                            <div className="mt-4 p-3 bg-gray-50 rounded">
                              <p className="text-sm font-medium text-gray-700 mb-2">Produits :</p>
                              <div className="space-y-1">
                                {projet.produits.slice(0, 3).map((produit, idx) => (
                                  <div key={idx} className="text-sm text-gray-600">
                                    • {produit.nom} ({produit.quantite}x)
                                  </div>
                                ))}
                                {projet.produits.length > 3 && (
                                  <div className="text-sm text-gray-500">
                                    + {projet.produits.length - 3} autre(s) produit(s)
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mt-4 flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.location.href = `/projets/${projet.id}`}
                            >
                              Voir détails
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-12">
                    <FolderOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-2">Aucun projet pour ce client</p>
                    <p className="text-sm text-gray-400">
                      Créez un nouveau projet pour commencer
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ONGLET SAV */}
            <TabsContent value="sav" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Tickets SAV ({clientSAV.length})</h3>
                <Button onClick={() => setShowSAVDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="w-4 h-4 mr-2" /> Nouveau Ticket
                </Button>
              </div>
              {clientSAV.length > 0 ? (
                <div className="space-y-3">
                  {clientSAV.map(ticket => (
                    <Card key={ticket.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold">{ticket.titre || ticket.type_intervention}</h4>
                            <p className="text-sm text-gray-600 mt-1">{ticket.description}</p>
                            <div className="flex gap-2 mt-2">
                              <Badge className={ticket.statut === 'terminee' ? 'bg-green-100 text-green-800' : ticket.statut === 'en_cours' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}>
                                {ticket.statut || 'Planifié'}
                              </Badge>
                              <Badge variant="outline">{ticket.priorite || 'normale'}</Badge>
                              <Badge variant="outline">{ticket.type_intervention}</Badge>
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">{formatDate(ticket.date_planifiee || ticket.created_at)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">Aucun ticket SAV</div>
              )}

              {/* Dialog Nouveau Ticket SAV */}
              <Dialog open={showSAVDialog} onOpenChange={setShowSAVDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Nouveau Ticket SAV</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Titre *</label>
                      <input className="w-full mt-1 px-3 py-2 border rounded-md" value={savForm.titre}
                        onChange={e => setSavForm({...savForm, titre: e.target.value})} placeholder="Ex: Panne onduleur" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Type</label>
                      <select className="w-full mt-1 px-3 py-2 border rounded-md" value={savForm.type_intervention}
                        onChange={e => setSavForm({...savForm, type_intervention: e.target.value})}>
                        <option value="maintenance">Maintenance</option>
                        <option value="depannage">Dépannage</option>
                        <option value="remplacement">Remplacement</option>
                        <option value="controle">Contrôle</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Priorité</label>
                      <select className="w-full mt-1 px-3 py-2 border rounded-md" value={savForm.priorite}
                        onChange={e => setSavForm({...savForm, priorite: e.target.value})}>
                        <option value="basse">Basse</option>
                        <option value="normale">Normale</option>
                        <option value="haute">Haute</option>
                        <option value="urgente">Urgente</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description</label>
                      <textarea className="w-full mt-1 px-3 py-2 border rounded-md" rows={3} value={savForm.description}
                        onChange={e => setSavForm({...savForm, description: e.target.value})} placeholder="Détails du problème..." />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowSAVDialog(false)}>Annuler</Button>
                      <Button onClick={handleCreateSAV} className="bg-orange-600 hover:bg-orange-700">Créer le Ticket</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* ONGLET DOCUMENTS */}
            <TabsContent value="documents">
              <DocumentManager 
                clientId={client.id} 
                clientName={`${currentClient.prenom} ${currentClient.nom}`}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Dialog de modification */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le client</DialogTitle>
          </DialogHeader>
          <ClientForm
            client={currentClient}
            onSave={handleSaveClient}
            onCancel={() => setShowEditDialog(false)}
            isEditing={true}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientDetails;
